import React, { useEffect, useRef, useState } from 'react';
import { Table, Select, Space, Pagination } from 'antd';
import styles from './index.module.less';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const CustomTable = ({
  data,
  pagination,
  showPaginationMeta = false,
  ...rest
}) => {
  const hasPagination =
    pagination !== false &&
    pagination !== null &&
    typeof pagination === 'object';

  const paginationConfig = hasPagination ? pagination : {};
  const total = paginationConfig.total || 0;
  const pageSize = paginationConfig.pageSize || 10;
  const onChange = paginationConfig.onChange;
  const size = paginationConfig.size;

  // `current` fallback: many list pages wire up an object-pagination + server
  // refetch but never pass `current`. Antd then pins the active-page highlight
  // to 1 forever, even though the rows underneath do change. When the caller
  // does NOT control `current`, track it here so the highlight follows the
  // user's clicks. A caller-provided `current` always wins (controlled mode).
  const isCurrentControlled = paginationConfig.current != null;
  const [internalCurrent, setInternalCurrent] = useState(1);

  // A change in `total` almost always means the underlying query changed
  // (search / filter), which conventionally resets to page 1. During pure page
  // navigation `total` is constant, so the tracked page is preserved. Only
  // relevant in uncontrolled mode; controlled callers manage their own reset.
  const prevTotalRef = useRef(total);
  useEffect(() => {
    if (prevTotalRef.current !== total) {
      prevTotalRef.current = total;
      if (!isCurrentControlled) setInternalCurrent(1);
    }
  }, [total, isCurrentControlled]);

  const maxPage = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(
    isCurrentControlled ? paginationConfig.current : internalCurrent,
    maxPage
  );

  // Prefer the `data` prop, but fall back to `dataSource` (passed via `...rest`
  // by most callers) so the empty-state styling never misfires.
  const rows = data ?? rest.dataSource;

  const start = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const end = Math.min(current * pageSize, total);

  return (
    <div
      className={`${styles.custom_table_container}${
        !rows?.length ? ` ${styles.no_data}` : ''
      }`}
    >
      <Table
        dataSource={data}
        pagination={hasPagination ? false : pagination}
        {...rest}
      />
      {hasPagination && (
        <div className={styles.pagination_footer}>
          {showPaginationMeta ? (
            <Space size={8}>
              {total > 0 && (
                <span style={{ color: '#595959', fontSize: 13 }}>
                  {`Showing ${start.toLocaleString()}–${end.toLocaleString()} of ${total.toLocaleString()} records`}
                </span>
              )}
              <span style={{ color: '#8c8c8c', fontSize: 13 }}>|</span>
              <span style={{ color: '#595959', fontSize: 13 }}>
                Rows per page:
              </span>
              <Select
                value={pageSize}
                onChange={(newSize) => {
                  if (!isCurrentControlled) setInternalCurrent(1);
                  onChange?.(1, newSize);
                }}
                style={{ width: 80 }}
                size="small"
                options={PAGE_SIZE_OPTIONS.map((v) => ({
                  label: String(v),
                  value: v
                }))}
              />
            </Space>
          ) : (
            <span />
          )}
          <Pagination
            {...paginationConfig}
            size={size || 'small'}
            total={total}
            pageSize={pageSize}
            current={current}
            showSizeChanger={false}
            showTotal={undefined}
            onChange={(p) => {
              if (!isCurrentControlled) setInternalCurrent(p);
              onChange?.(p, pageSize);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CustomTable;
